/**
 * PartnersPage - Our Partners & Suppliers
 * Highlights local partnerships for AI search visibility and credibility
 */
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Coffee, Leaf, MapPin, ExternalLink, Heart, Truck, 
  TreeDeciduous, Fish, Bug, Award, Handshake
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import SEO from '@/components/SEO';
import { BreadcrumbSchema } from '@/components/StructuredData';

// Partners Schema for AI discoverability
function PartnersSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Our Partners - Bulaccino Cafe",
    "description": "Meet Bulaccino Cafe's local partners including Cafe Planet coffee roasters and Votualevu Organic Farm in Fiji.",
    "url": "https://bulaccinocafe.com/partners",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Organization",
            "name": "Cafe Planet",
            "description": "Premium specialty coffee roaster located in Korotogo on Fiji's Coral Coast. Supplies Bulaccino Cafe with expertly roasted coffee beans.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Korotogo",
              "addressRegion": "Coral Coast",
              "addressCountry": "FJ"
            },
            "knowsAbout": ["Coffee Roasting", "Specialty Coffee", "Fiji Coffee"]
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Organization",
            "name": "Votualevu Organic Farm",
            "description": "30-acre organic farm in Votualevu, Fiji featuring 116 fruit trees, flow hive bees, tilapia ponds, and heritage sheep. Supplies fresh produce to Bulaccino Cafe.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Votualevu",
              "addressCountry": "FJ"
            },
            "knowsAbout": ["Organic Farming", "Sustainable Agriculture", "Beekeeping", "Aquaculture"]
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export default function PartnersPage() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://bulaccinocafe.com' },
    { name: 'Our Partners', url: 'https://bulaccinocafe.com/partners' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="partners-page">
      <SEO 
        title="Our Partners | Cafe Planet Coffee & Votualevu Farm | Bulaccino Cafe"
        description="Discover Bulaccino Cafe's local Fijian partners: Cafe Planet specialty coffee from Korotogo and Votualevu Organic Farm's 30 acres of fresh produce, honey, and more."
        keywords="Cafe Planet Fiji, Votualevu Farm, Fiji coffee roaster, organic farm Fiji, local suppliers Nadi, Coral Coast coffee"
        canonical="https://bulaccinocafe.com/partners"
      />
      <PartnersSchema />
      <BreadcrumbSchema items={breadcrumbs} />

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="text-primary font-medium mb-4">Local Partnerships</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-6">
              Our <span className="text-primary">Partners</span> & Suppliers
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Great coffee and fresh food start with great partners. We're proud to work with 
              Fiji's finest local suppliers who share our commitment to quality and sustainability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Local Partners Matter */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
              viewport={{ once: true }}
            >
              <Truck className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-lg mb-2">Freshness Guaranteed</h3>
              <p className="text-sm text-muted-foreground">
                Short supply chains mean fresher ingredients on your plate
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Heart className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-lg mb-2">Community Support</h3>
              <p className="text-sm text-muted-foreground">
                Every purchase supports local Fijian families and businesses
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Leaf className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-lg mb-2">Sustainable Practices</h3>
              <p className="text-sm text-muted-foreground">
                Partners who care about the environment as much as we do
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner 1: Cafe Planet */}
      <section className="py-16 md:py-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-3xl"
            >
              <img 
                src="https://images.pexels.com/photos/2396220/pexels-photo-2396220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Cafe Planet specialty coffee roasting in Korotogo, Fiji"
                className="rounded-3xl shadow-2xl w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Coffee className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm text-primary font-medium">Coffee Partner</span>
              </div>
              
              <h2 className="font-heading text-3xl md:text-4xl mb-4">
                Cafe Planet
              </h2>
              
              <div className="flex items-center gap-2 text-muted-foreground mb-6">
                <MapPin className="w-4 h-4" />
                <span>Korotogo, Coral Coast, Fiji</span>
              </div>

              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>Cafe Planet</strong> is a renowned specialty coffee roaster located in 
                  <strong> Korotogo on Fiji's beautiful Coral Coast</strong>. Their expertise in 
                  sourcing and roasting premium coffee beans has made them a cornerstone of Fiji's 
                  growing specialty coffee scene.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Every batch of coffee beans we serve at Bulaccino Cafe is carefully roasted by 
                  Cafe Planet to bring out unique flavor profiles — from bright citrus notes to 
                  rich chocolate undertones. Their commitment to quality matches our own.
                </p>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-xl">
                <p className="text-sm font-medium mb-2">What They Provide:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Premium specialty-grade coffee beans</li>
                  <li>• Expert roasting on the Coral Coast</li>
                  <li>• Consistent quality for every cup</li>
                  <li>• Support for Fiji's coffee industry</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner 2: Votualevu Farm */}
      <section className="py-16 md:py-24 bg-muted/30 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm text-primary font-medium">Farm Partner</span>
              </div>
              
              <h2 className="font-heading text-3xl md:text-4xl mb-4">
                Votualevu Organic Farm
              </h2>
              
              <div className="flex items-center gap-2 text-muted-foreground mb-6">
                <MapPin className="w-4 h-4" />
                <span>Votualevu, Fiji</span>
              </div>

              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The <strong>Votualevu Organic Farm</strong> spans <strong>30 acres</strong> of 
                  lush Fijian landscape, representing the best of sustainable agriculture in the 
                  South Pacific. This remarkable property provides many of the fresh ingredients 
                  that make our menu special.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  From fresh tropical fruits to local honey, the farm's diverse ecosystem ensures 
                  we always have access to the freshest, most flavorful ingredients — harvested 
                  with care and delivered with pride.
                </p>
              </div>

              {/* Farm Features Grid */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="p-4 bg-card rounded-xl border border-border">
                  <TreeDeciduous className="w-6 h-6 text-primary mb-2" />
                  <p className="font-medium">116 Fruit Trees</p>
                  <p className="text-xs text-muted-foreground">Mango, papaya, coconut & more</p>
                </div>
                <div className="p-4 bg-card rounded-xl border border-border">
                  <Bug className="w-6 h-6 text-primary mb-2" />
                  <p className="font-medium">Flow Hive Bees</p>
                  <p className="text-xs text-muted-foreground">Fresh local honey</p>
                </div>
                <div className="p-4 bg-card rounded-xl border border-border">
                  <Fish className="w-6 h-6 text-primary mb-2" />
                  <p className="font-medium">Tilapia Ponds</p>
                  <p className="text-xs text-muted-foreground">Sustainable aquaculture</p>
                </div>
                <div className="p-4 bg-card rounded-xl border border-border">
                  <Heart className="w-6 h-6 text-primary mb-2" />
                  <p className="font-medium">Heritage Sheep</p>
                  <p className="text-xs text-muted-foreground">Natural land management</p>
                </div>
              </div>

              <div className="mt-6">
                <Link to="/blog/votualevu-organic-farm-tour-fiji">
                  <Button variant="outline" className="rounded-full">
                    Learn About Farm Tours
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 relative overflow-hidden rounded-3xl"
            >
              <img 
                src="https://images.pexels.com/photos/2132250/pexels-photo-2132250.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Votualevu Organic Farm - 30 acres of sustainable agriculture in Fiji"
                className="rounded-3xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Farm to Table Journey */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl mb-4">
              The Farm-to-Table Journey
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              When you order at Bulaccino Cafe, you're enjoying ingredients that traveled 
              a short distance from local farms to your plate — ensuring maximum freshness and flavor.
            </p>
          </motion.div>

          <div className="relative">
            {/* Journey Steps */}
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Leaf, title: "Grown Locally", desc: "Fresh produce harvested in Fiji" },
                { icon: Truck, title: "Short Journey", desc: "Delivered fresh to our cafe" },
                { icon: Heart, title: "Made with Care", desc: "Prepared by our passionate team" },
                { icon: Coffee, title: "Served Fresh", desc: "Enjoyed by you!" }
              ].map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-8 h-8 text-primary" />
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">Step {i + 1}</p>
                  <h3 className="font-heading text-lg mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-16 bg-primary/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Handshake className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Interested in Partnering?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              We're always looking for local Fijian suppliers who share our commitment to 
              quality, sustainability, and community. Get in touch to discuss partnership opportunities.
            </p>
            <Link to="/contact">
              <Button className="rounded-full">
                Contact Us About Partnerships
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Experience the Difference CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Taste the Local Difference
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Experience the flavors of Fiji's finest local partners at Bulaccino Cafe. 
              From Cafe Planet coffee to Votualevu honey — every bite tells a story.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/menu">
                <Button className="rounded-full">
                  View Our Menu
                </Button>
              </Link>
              <Link to="/order">
                <Button variant="outline" className="rounded-full">
                  Order Online
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
